<template>
    <modal
        :id="modalId"
        :title="title"
        :ok-disabled="loading"
        ok-title="Close"
        @hide="hideModal"
        ok-only
        @show="getModules"
    >
        <busy-modal :show="loading"></busy-modal>
        <b-row>
            <b-col md="12">
                <div class="alert alert-info" role="alert">
                    <p>
                        The modules listed below are enhancements to your basic subscription. They can be added or
                        removed from your account at any time. Module prices are monthly and will be billed at the same
                        time as your primary subscription.
                    </p>
                    <p>
                        If a module is added AFTER the primary subscription trial period expires, then your account will
                        be immediately billed a prorated amount for the module(s).
                    </p>
                </div>

                <grid
                    ref="grid"
                    :column-defs="columnDefs"
                    :is-auto-height="true"
                    :is-search="false"
                    :row-data="modules"
                    :is-auto-width="true"
                    :offset-top="200"
                    :get-row-height="getRowHeight"
                >
                </grid>
            </b-col>
        </b-row>
        <modal
            :id="confirmModuleUpgradeModalId"
            title="Confirm Module Upgrade"
            ok-title="Confirm"
            @ok="addModule"
            @cancel="toggleModuleBack"
            :static="true"
            >You will be charged ${{ upgradeModulePrice }} immediately. Do you want to continue?</modal
        >
    </modal>
</template>

<script>
    import isNil from 'lodash/isNil';
    import orderBy from 'lodash/orderBy';
    import clone from 'lodash/clone';
    import API from '../../../api';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import Modal from '../../generic/modal/modal.vue';
    import { formatResponseError } from '../../../utils/api';
    import grid from '../../generic/grid/grid.vue';
    import ModulesActionsCellRenderer from '../common/modules-actions-cell-renderer.vue';
    import {
        SUBSCRIPTION_BILLING_ADD_MODULE_ENDPOINT,
        SUBSCRIPTION_BILLING_REMOVE_MODULE_ENDPOINT,
    } from '../../../endpoints';

    export default {
        name: 'SubscriptionModuleChangeModal',
        components: {
            BusyModal,
            Modal,
            grid,
            modulesActionsCellRenderer: ModulesActionsCellRenderer,
        },
        props: {
            modalId: String,
            title: String,
        },
        computed: {
            columnDefs() {
                return [
                    {
                        field: 'description',
                        headerName: 'Description',
                        sortable: false,
                        resizable: false,
                        minWidth: 250,
                    },
                    {
                        field: 'max_properties',
                        headerName: 'Max. Properties',
                        resizable: false,
                        sortable: false,
                        minWidth: 130,
                        maxWidth: 130,
                    },
                    {
                        field: 'amount',
                        headerName: 'Price',
                        sortable: false,
                        resizable: false,
                        minWidth: 80,
                        maxWidth: 80,
                    },
                    {
                        field: 'billing_period_string',
                        headerName: 'Billing',
                        sortable: false,
                        resizable: false,
                        minWidth: 90,
                        maxWidth: 90,
                    },
                    {
                        headerName: '',
                        minWidth: 100,
                        maxWidth: 100,
                        sortable: false,
                        resizable: false,
                        cellRenderer: 'modulesActionsCellRenderer',
                        cellRendererParams: {
                            selectedModulesGetter: () => this.selectedModules,
                            onToggleModule: async (value, module) => {
                                this.toggleModule(value, module);
                            },
                        },
                    },
                ];
            },
        },
        data() {
            return {
                loading: false,
                modules: null,
                selectedModules: {},
                enabled: false,
                subscription: null,
                upgradeModule: null,
                upgradeModulePrice: null,
                confirmModuleUpgradeModalId: 'confirm-module-upgrade-modal',
            };
        },
        methods: {
            hideModal() {
                this.$bvModal.hide(this.modalId);
            },
            async confirm() {
                this.loading = true;
                try {
                    await API.put(SUBSCRIPTION_BILLING_CANCEL_SUBSCRIPTION_ENDPOINT(), this.object);

                    this.$emit('success-cancel');

                    this.$nextTick(() => {
                        this.hideModal();
                    });
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text: generic || 'Something went wrong while cancelling subscription.',
                    });
                } finally {
                    this.loading = false;
                }
            },
            async getModules() {
                this.subscription = this.$parent.$parent.subscription;
                this.modules = clone(orderBy(this.subscription.package.modules, ['predicate'], ['desc'])).filter(
                    item => item.component !== 'website' && item.component !== 'verification'
                );
                this.selectedModules = {};
                for (var i = 0; i < this.modules.length; i++) {
                    this.selectedModules[this.modules[i].id] = false;
                }
                for (var i = 0; i < this.subscription.modules.length; i++) {
                    if (!this.subscription.modules[i].is_scheduled_for_removal) {
                        this.selectedModules[this.subscription.modules[i].module_id] = true;
                    }
                }
            },
            async toggleModule(value, module) {
                this.selectedModules[module.id] = value;
                this.upgradeModule = module;
                this.upgradeModulePrice = this.subscription.module_upgrade_prices[module.id];

                if (value && this.subscription.module_upgrade_prices[module.id] > 0) {
                    this.$bvModal.show(this.confirmModuleUpgradeModalId);
                } else if (value) {
                    this.addModule();
                } else if (!value) {
                    this.removeModule();
                }
            },
            async addModule() {
                this.loading = true;
                try {
                    await API.put(SUBSCRIPTION_BILLING_ADD_MODULE_ENDPOINT(this.upgradeModule.id));
                    this.successToast({ text: `${this.upgradeModule.description} successfully added.` });
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text: generic || 'Something went wrong while adding module.',
                    });
                } finally {
                    await this.$parent.$parent.fetchSuscriptionBillingData();
                    this.getModules();
                    this.loading = false;
                }
            },
            async removeModule() {
                this.loading = true;
                try {
                    await API.put(SUBSCRIPTION_BILLING_REMOVE_MODULE_ENDPOINT(this.upgradeModule.id));
                    this.successToast({ text: `${this.upgradeModule.description} successfully removed.` });
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text: generic || 'Something went wrong while removing module.',
                    });
                } finally {
                    await this.$parent.$parent.fetchSuscriptionBillingData();
                    this.getModules();
                    this.loading = false;
                }
            },
            toggleModuleBack() {
                this.$set(this.selectedModules, this.upgradeModule.id, false);
            },
            getRowHeight() {
                return 45;
            },
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.modal-dialog) {
        max-width: 665px !important;
    }

    :deep(.ag-cell-wrapper) {
        display: block !important;
    }

    :deep(.ag-center-cols-clipper) {
        min-height: unset !important;
    }
</style>
